import styled, { css } from "styled-components";
import { borderRadius, mediaQueries, spacing, typographyMixin } from "@magicbi/ui-library";
import type { FieldMappingStatus } from "./FieldMappingList.types";

export const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
`;

const grid = css`
  display: grid;
  grid-template-columns: minmax(0, 11.875rem) 1.125rem minmax(0, 13.75rem) minmax(0, 1fr);
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};

  ${mediaQueries.down.sm} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing[1]};
  }
`;

const sampleFirstGrid = css`
  grid-template-columns: minmax(0, 11.875rem) minmax(0, 1fr) minmax(0, 13.75rem) minmax(0, 10rem);

  ${mediaQueries.down.sm} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Row = styled.li<{ $sampleFirst?: boolean }>`
  ${grid}
  ${({ $sampleFirst }) => $sampleFirst && sampleFirstGrid}
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};

  &:first-child {
    border-top: 0;
  }

  > svg {
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.colors.text.secondary};

    ${mediaQueries.down.sm} {
      display: none;
    }
  }
`;

export const HeadRow = styled.li<{ $sampleFirst?: boolean; $plain?: boolean }>`
  ${grid}
  ${({ $sampleFirst }) => $sampleFirst && sampleFirstGrid}
  padding-top: ${spacing[1]};
  padding-bottom: ${spacing[1]};
  background: ${({ theme }) => theme.colors.background.tertiary};
  ${typographyMixin("overline", 4)}
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  ${({ $plain }) =>
    $plain &&
    css`
      ${typographyMixin("caption", 2)}
      text-transform: none;
      padding-top: ${spacing[2]};
      padding-bottom: ${spacing[2]};
      background: ${({ theme }) => theme.colors.surface.primary};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
    `}

  ${mediaQueries.down.sm} {
    display: none;
  }
`;

export const Field = styled.div`
  min-width: 0;
`;

export const FieldLabel = styled.label`
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Required = styled.span`
  margin-left: ${spacing[0.5]};
  color: ${({ theme }) => theme.colors.error.text};
`;

export const FieldSub = styled.span`
  display: block;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Check = styled.div`
  display: grid;
  min-width: 0;
`;

export const Status = styled.span<{ $status: FieldMappingStatus }>`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  min-width: 0;
  ${typographyMixin("caption", 2)}
  color: ${({ theme, $status }) =>
    $status === "ok"
      ? theme.colors.success.text
      : $status === "need"
        ? theme.colors.error.text
        : $status === "warn"
          ? theme.colors.text.primary
          : theme.colors.text.secondary};

  svg {
    flex: none;
    width: 0.875rem;
    height: 0.875rem;
    color: ${({ theme, $status }) => ($status === "warn" ? theme.colors.warning[500] : "currentColor")};
  }
`;

export const Sample = styled.span<{ $plain?: boolean }>`
  ${({ $plain }) => ($plain ? typographyMixin("caption", 3) : typographyMixin("code", "small"))}
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Footer = styled.li`
  padding: ${spacing[2]} ${spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;
